/**
 * SPEC.md §10, §11 — M6.1 (issue #28). A single source of truth for every
 * keyboard shortcut this app binds: the `?` cheatsheet renders from this
 * list, so it can never drift out of sync with what's actually wired.
 *
 * This module only *describes* shortcuts for display — it doesn't bind any
 * of them. Each existing handler (App.tsx's Cmd/Ctrl+Enter, +Shift+S,
 * +Shift+C; Preview.tsx's +Shift+F) keeps matching its own keydown event
 * independently; adding an entry here is documentation, not wiring.
 */

/** A normalized key token: `'mod'` is Cmd on Mac, Ctrl elsewhere — never hardcode one or the other. */
export type ShortcutKey = 'mod' | 'shift' | 'enter' | string;

export interface ShortcutEntry {
  id: string;
  keys: readonly ShortcutKey[];
  description: string;
}

export const SHORTCUTS: ShortcutEntry[] = [
  { id: 'command-palette', keys: ['mod', 'k'], description: 'Command palette' },
  { id: 'force-render', keys: ['mod', 'enter'], description: 'Force re-render' },
  { id: 'save-svg', keys: ['mod', 'shift', 's'], description: 'Save SVG' },
  { id: 'copy-png', keys: ['mod', 'shift', 'c'], description: 'Copy PNG to clipboard' },
  { id: 'fit-to-view', keys: ['mod', 'shift', 'f'], description: 'Fit to view' },
  { id: 'toggle-comment', keys: ['mod', '/'], description: 'Toggle comment' },
  { id: 'toggle-pane', keys: ['mod', 'b'], description: 'Switch Source/Preview tab' },
  { id: 'shortcut-cheatsheet', keys: ['?'], description: 'Shortcut cheatsheet' },
];

const MAC_LABELS: Record<string, string> = { mod: '⌘', shift: '⇧', enter: '⏎' };
const OTHER_LABELS: Record<string, string> = { mod: 'Ctrl', shift: 'Shift', enter: 'Enter' };

const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA']);

/**
 * True when a keydown's `event.target` is somewhere that accepts text
 * input — a native form control, a contentEditable region, or inside
 * CodeMirror's own content root. Global single-key shortcuts (`?`) must
 * check this before acting, or they'd hijack a character the user is
 * legitimately typing.
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (EDITABLE_TAGS.has(target.tagName)) return true;
  if (target.closest('[contenteditable=""], [contenteditable="true"]') !== null) return true;
  if (target.closest('.cm-content') !== null) return true;
  return false;
}

export function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad/.test(navigator.userAgent);
}

/**
 * `isMac` defaults to real platform detection at the call site but is an
 * explicit parameter, not baked into the function body, so both branches
 * are testable without mocking `navigator`.
 */
export function formatShortcut(keys: readonly ShortcutKey[], isMac: boolean = isMacPlatform()): string {
  const labels = keys.map((key) => {
    const named = (isMac ? MAC_LABELS : OTHER_LABELS)[key];
    if (named !== undefined) return named;
    return key.length === 1 ? key.toUpperCase() : key;
  });
  return isMac ? labels.join('') : labels.join('+');
}
